import { useEffect, useState } from "react"
import { FaEye } from "react-icons/fa"
import CashierHistoryModal from "./CashierHistoryModal"
import { CashierOrderHistory } from "@/types/ApiTypes"
import { IoCashOutline } from "react-icons/io5"
import { CiCreditCard1 } from "react-icons/ci"
import { AppConfig } from "@/utils/AppConfig"
import { useSelector } from "react-redux"
import axios from "axios"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next";
import { formatMoney } from "@/utils/helpers"
import { RootState } from "@/store/store"
import useLockBodyScroll from "@/hooks/useLockBodyScroll"

interface CashierBodyProps {
    order: CashierOrderHistory
    setRefetch: (refetch: boolean) => void;
}


export interface DateTimeFormatOptions {
    timestamp: number;

}
// ... keep imports as they are

export default function CashierHistoryBody({ order, setRefetch }: CashierBodyProps) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const [cashierModalOpen, setCashierModalOpen] = useState(false);
    const token = useSelector((state: { auth: { token: string } }) => state.auth.token);
    const baseUrl = import.meta.env.VITE_BASE_URL;
    const [selectedStatus, setSelectedStatus] = useState("");
    const [formattedDateEnglish, setFormattedDateEnglish] = useState("");
    const [formattedDateArabic, setFormattedDateArabic] = useState("");
    const [orderProgress, setOrderProgress] = useState(order.progress || "");
    const userData = useSelector((state: RootState) => state.user.userData)
    useLockBodyScroll(cashierModalOpen)
    function toggleCashierModal() {
        setCashierModalOpen(!cashierModalOpen);
    }


    async function updateStatus() {

        if (order.progress === "cancelled" || order.progress === "delivered") {
            toast.error(t("cashierHistoryBody.cannotUpdateStatus") as string);
            return;
        }
        const toastId = toast.loading(t("cashierHistoryBody.updating") as string);
        try {
            const options = {
                url: `${baseUrl}/api/dashboard/cashier/orders/${order._id}/update`,
                method: "PUT",
                headers: { Authorization: token },
                data: { status: "returned" }
            };
            const response = await axios.request(options);
            if (response.data.success) {
                toast.success(t("cashierHistoryBody.updateSuccess") as string);
                setRefetch(true);
            }
        } catch (error) {
            console.error(error);
        } finally {
            toast.dismiss(toastId);
        }
    }

    async function updateOrderProgress({ id, progress }: { id: string; progress: string }) {

        try {
            const options = {
                url: `${baseUrl}/api/dashboard/cashier/orders/${id}/update?locale=${locale}`,
                method: "PUT",
                headers: { Authorization: token },
                data: { progress }
            };
            const response = await axios.request(options);
            if (response.data.success) {
                toast.success(t("ordersBody.OrderProgressUpdatedSuccessfully") as string);
                setRefetch(true);
            }
        } catch (error) {
            console.error(error);
        }
    }

    async function cancelOrder({ id }: { id: string }) {

        try {
            const options = {
                url: `${baseUrl}/api/dashboard/cashier/orders/${id}/cancel`,
                method: "POST",
                headers: { Authorization: token }
            };
            const response = await axios.request(options);
            if (response.data.success) {
                toast.success(t("ordersBody.OrderCanceledSuccessfully") as string);
                setRefetch(true);
            }
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        setOrderProgress(order.progress || "");
        setSelectedStatus(order.status);
    }, [order]);

    const formatDateTime = ({ timestamp }: DateTimeFormatOptions) => {
        const date = new Date(timestamp * 1000);
        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
        };

        const formattedEnglish = date.toLocaleString("en-US", options);
        const formattedArabicRaw = date.toLocaleString("ar-EG", options);
        const arabicNumbers = "٠١٢٣٤٥٦٧٨٩";
        const englishNumbers = "0123456789";

        const formattedArabic = formattedArabicRaw.replace(/[٠-٩]/g, (d) =>
            englishNumbers[arabicNumbers.indexOf(d)]
        );

        setFormattedDateEnglish(formattedEnglish);
        setFormattedDateArabic(formattedArabic);
    };

    useEffect(() => {
        formatDateTime({ timestamp: order.time });
    }, [order.time]);


    return (
        <>
            {cashierModalOpen && <CashierHistoryModal order={order} toggleCashierModal={toggleCashierModal} />}
            <tr className="border-b">
                <td className="px-6 py-4 text-center">
                    {order.customer ? order.customer.name : t("cashierHistoryBody.guest")}
                </td>

                <td className="px-6 py-4 text-center flex flex-col">
                    {order._id}
                    <span>{t("cashierHistoryBody.order")}# {order?.invoice?.orderId}</span>
                </td>
                {userData?.role === "owner" || userData?.role === "superadmin" ? (
                    <td className="px-6 py-4 hidden md:table-cell">
                        {t(order?.invoice.branch.nameEN, order?.invoice.branch.nameAR)}
                    </td>
                ) : null}

                <td className="px-6 py-4 text-center">
                    {locale === "en" ? formattedDateEnglish : formattedDateArabic}
                </td>
                <td className="px-6 py-4 text-center">
                    <span className="flex flex-col items-center gap-1">
                        {order.cashPaid > 0 && (
                            <span className="flex items-center gap-1">
                                {t("cashierHistoryBody.cash")}
                                <IoCashOutline className="text-green-700 text-lg" />
                                <span className="text-green-700">{formatMoney(order.cashPaid)} {AppConfig.currency.icon()}</span>
                            </span>
                        )}
                        {order.networkPaid > 0 && (
                            <span className="flex items-center gap-1">
                                {t("cashierHistoryBody.creditCard")}
                                <CiCreditCard1 className="text-blue-700 text-lg" />
                                <span className="text-blue-700">{formatMoney(order.networkPaid)} {AppConfig.currency.icon()}</span>
                            </span>
                        )}
                        {order.usedWalletBalance > 0 && (
                            <span className="flex items-center gap-1 text-green-600">
                                {t("cashierHistoryModal.usedWalletBalance")}
                                <span>{formatMoney(order.usedWalletBalance)} {AppConfig.currency.icon()}</span>
                            </span>
                        )}
                        {order.couponDiscount > 0 && (
                            <span className="flex items-center gap-1 text-green-600">
                                {t("cashierHistoryModal.usedCouponDiscount")}
                                <span>{formatMoney(order.couponDiscount)} {AppConfig.currency.icon()}</span>
                            </span>
                        )}
                    </span>
                </td>
                <td className="py-4 text-center">
                    {orderProgress === "cancelled" || order.status === "returned" || order.status === "cancelled" ? (
                        <div className="bg-red-500 px-2 py-[2px] mx-auto rounded-xl w-fit text-white">
                            {t("ordersBody.Cancelled")}
                        </div>
                    ) : orderProgress === "delivered" ? (
                        <div className="w-fit mx-auto">
                            <span className="px-2 py-[2px] rounded-xl text-white bg-green-600">
                                {t("ordersBody.delivered")}
                            </span>
                        </div>
                    ) : (orderProgress === null || orderProgress === "") && order.status !== "accepted" ? (
                        <div className="w-fit px-3 py-1 mx-auto rounded-2xl bg-blue-400 text-white">
                            {t("ordersBody.pending")}
                        </div>
                    ) : (
                        <div className="border mx-auto px-2 py-[2px] rounded-xl w-fit">
                            <select
                                defaultValue={orderProgress || ""}
                                onChange={(e) => {
                                    if (e.target.value === "cancelled") {
                                        cancelOrder({ id: order._id });
                                    } else {
                                        const value = e.target.value;
                                        setOrderProgress(value);
                                        updateOrderProgress({ id: order._id, progress: value });
                                    }
                                }}
                            >
                                <option value="">{t("ordersBody.accepted")}</option>
                                <option value="preparing">{t("ordersBody.preparing")}</option>
                                <option value="finished">{t("ordersBody.finished")}</option>
                                <option value="delivered">{t("ordersBody.delivered")}</option>
                                <option value="cancelled" className="text-red-400 font-semibold">
                                    {t("ordersBody.cancelorders")}
                                </option>
                            </select>
                        </div>
                    )}
                </td>
                <td className="py-4 text-center">
                    {selectedStatus === "returned" || order.status === "cancelled" ? (
                        <span className="text-white bg-red-600 px-2 py-[2px] rounded-xl">
                            {t("cashierHistoryBody.returned")}
                        </span>
                    ) : (
                        <div className="border mx-auto px-2 py-[2px] rounded-xl w-fit">
                            <select
                                onChange={(e) => {
                                    setSelectedStatus(e.target.value);
                                    if (e.target.value !== "paid") updateStatus();
                                }}
                                className="w-20"
                            >
                                <option value="paid">{t("cashierHistoryBody.paid")}</option>
                                <option value="returned">{t("cashierHistoryBody.returned")}</option>
                            </select>
                        </div>

                    )}
                </td>
                <td className="px-6 py-4 text-center flex flex-col">
                    <span dir="rtl">
                        {t("cashierHistoryBody.currencyAmount", { value: formatMoney(order.total) })} {AppConfig.currency.icon()}
                    </span>
                    <span className={`${order.status !== "returned" ? "text-green-600" : "text-red-600"}`}>
                        {order.status !== "returned" ? t("cashierHistoryBody.paid") : t("cashierHistoryBody.returned")}
                    </span>
                </td>
                <td className="py-4 text-center space-y-2">
                    <span
                        onClick={toggleCashierModal}
                        className="px-2 py-[2px] rounded-xl w-16 transition-colors duration-200 text-white cursor-pointer text-center hover:bg-blue-700 bg-blue-600 text-[10px] flex justify-center items-center mx-auto gap-1"
                    >
                        {t("cashierHistoryBody.preview")}
                        <FaEye className="text-sm" />
                    </span>
                </td>
            </tr>
        </>
    );
}
